import { useEffect, useMemo, useState } from "react";
import Page from "../../../../components/Layout/Page";
import { useIsMobile } from "../../../../helpers/ui";
import { colors } from "../../../../styles";
import { DesktopHeader, MobileHeader } from "../Account/components";
import { employeeAccountPath } from "../../../../routes/paths";
import HeaderBreadcrumbs from "../../../../components/HeaderBreadcrumbs";
import T from "../../../../components/Typography";
import ResponsePageContent from "../../../../components/Layout/ResponsivePageContent";
import { useNavigate } from "react-router-dom";
import {
  SectionFlexColumn,
  SectionFlexRow,
} from "../../../../components/General";
import useGetAccount from "../../../../hooks/queries/employee/useGetAccount";
import Loader from "../../../../components/Loader";
import Button from "../../../../components/Button";
import ErrorPage from "../../../../components/ErrorPage";
import Slider from "../../../../components/Slider";
import {
  formatCurrency,
  monthlyAmountToBiWeekly,
  monthlyAmountToWeekly,
} from "../../../../helpers/currency";
import Checkbox from "../../../../components/Checkbox";
import useUpdateContribution from "../../../../hooks/mutations/employee/useUpdateContribution";
import { toast } from "react-toastify";

export const calculatePotentialSavings = (
  contributionsSoFar: number,
  contribution: number,
  monthsLeftInProgram: number
) => {
  return contributionsSoFar + contribution * monthsLeftInProgram;
};

const ChangeContribution = () => {
  const { data, status } = useGetAccount();
  const { mutateAsync: updateContribution, status: updateContributionStatus } =
    useUpdateContribution();
  const [contribution, setContribution] = useState("");
  const [agreedToSetAside, setAgreedToSetAside] = useState(false);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const title = "Change Contribution";
  const subtitle = "Set up a recurring deposit";
  const breadcrumbs = ["My Account", title];

  useEffect(() => {
    if (
      data?.challenger_saving.sponsored_savings.ssp_enrollment
        ?.ssp_ygbm_monthly_contribution
    ) {
      setContribution(
        data.challenger_saving.sponsored_savings.ssp_enrollment
          .ssp_ygbm_monthly_contribution
      );
    }
  }, [data]);

  const program = data?.challenger_saving.sponsored_savings;

  const contributionsSoFar =
    Number(
      program?.ssp_enrollment?.sponsored_savings_status
        .current_program_net_contributions
    ) || 0;

  const monthsLeftInProgram =
    program?.ssp_enrollment?.sponsored_savings_status
      .current_program_months_till_end || 1;

  const potentialSavingsToDisplayToUser = useMemo(
    () =>
      calculatePotentialSavings(
        contributionsSoFar,
        Number(contribution),
        monthsLeftInProgram
      ),
    [contributionsSoFar, contribution, monthsLeftInProgram]
  );

  if (status === "loading") return <Loader />;
  if (updateContributionStatus === "loading")
    return <Loader text="Updating contribution..." />;
  if (status === "error") return <ErrorPage />;

  const doBack = () => {
    navigate(employeeAccountPath);
  };

  const handleSave = async () => {
    const programEnrollmentId = program?.ssp_enrollment?.id;
    if (!programEnrollmentId) return;
    const response = await updateContribution({
      contribution: contribution,
      programEnrollmentId: programEnrollmentId,
      directDepositUserAgreed: agreedToSetAside,
    });
    if (response?.id) {
      toast.success("Contribution updated");
    }
  };

  const valuetext = (value: number) => {
    return `$${value}`;
  };

  const biweekly = monthlyAmountToBiWeekly(Number(contribution));
  const weekly = monthlyAmountToWeekly(Number(contribution));
  const formattedContribution = formatCurrency(contribution, 0);
  const employerSupportsAutoSwitch =
    data.employer.sponsored_savings_autoswitch_directdeposit;
  const minAmountToHitGoal =
    Number(
      program?.ssp_enrollment?.sponsored_savings_status
        .current_program_min_contribution_to_hit_goal
    ) || 0;

  const maxContribution = 250;
  return (
    <Page>
      {isMobile ? (
        <MobileHeader
          title={title}
          showBack
          showClose
          handleBack={doBack}
          closeRoutePath={employeeAccountPath}
        />
      ) : (
        <DesktopHeader>
          <HeaderBreadcrumbs breadcrumbs={breadcrumbs} handleBack={doBack} />
        </DesktopHeader>
      )}
      <ResponsePageContent title={title} subtitle={subtitle}>
        <SectionFlexColumn sx={{ alignItems: "center" }}>
          <div
            style={{ width: isMobile ? "100%" : "50%", textAlign: "center" }}
          >
            <SectionFlexColumn>
              <T variant="headingLg" color={colors.black}>
                {formattedContribution} Monthly
              </T>
              <SectionFlexRow sx={{ justifyContent: "center" }}>
                <T variant="lgReg" color={colors.gray_600}>
                  {formatCurrency(biweekly, 2)} Biweekly,{" "}
                  {formatCurrency(weekly, 2)} Weekly
                </T>
              </SectionFlexRow>
            </SectionFlexColumn>
            <SectionFlexRow>
              <Slider
                min={30}
                max={maxContribution}
                marks={[
                  { value: 30, label: "$30" },
                  { value: maxContribution, label: `$${maxContribution}` },
                ]}
                getAriaValueText={valuetext}
                valueLabelDisplay="off"
                valueLabelFormat={valuetext}
                value={Number(contribution)}
                onChange={(e) => {
                  const el = e.target as HTMLInputElement;
                  setContribution(el.value);
                }}
                sx={{
                  color: colors.light_green,
                  "& .MuiSlider-thumb": {
                    height: 20,
                    width: 30,
                    backgroundColor: colors.teal,
                    "&:before": {
                      display: "none",
                    },
                    borderRadius: 10,
                  },
                }}
              />
            </SectionFlexRow>
            <SectionFlexColumn sx={{ marginTop: "32px" }}>
              {program?.ssp_enrollment?.sponsored_savings_status
                .current_program_status === "offtrack" &&
                minAmountToHitGoal <= maxContribution && (
                  <T
                    variant="smReg"
                    color={colors.red}
                    sx={{ marginBottom: "8px" }}
                  >
                    {`Increase your contribution to ${formatCurrency(
                      minAmountToHitGoal
                    )} / month to get the match.`}
                  </T>
                )}
              <T variant="mdReg">
                {` With a contribution of ${formattedContribution}, you'll reach a savings balance of
                ${formatCurrency(
                  potentialSavingsToDisplayToUser
                )} in the remaining ${monthsLeftInProgram} months of this program. This includes what you've already
                saved.`}
              </T>
            </SectionFlexColumn>
            {employerSupportsAutoSwitch && (
              <SectionFlexRow
                onClick={() => setAgreedToSetAside(!agreedToSetAside)}
                sx={{
                  textAlign: "initial",
                  cursor: "pointer",
                  gap: "8px",
                  marginTop: "64px",
                }}
              >
                <Checkbox checked={agreedToSetAside} />
                <T
                  variant="smReg"
                  color={colors.gray_600}
                >{`I agree to allow my employer to set aside ${formattedContribution}/month from my paycheck to this account.`}</T>
              </SectionFlexRow>
            )}
            <Button
              noMargin
              sx={{ marginTop: "32px" }}
              disabled={employerSupportsAutoSwitch && !agreedToSetAside}
              onClick={handleSave}
            >
              Save
            </Button>
          </div>
        </SectionFlexColumn>
      </ResponsePageContent>
    </Page>
  );
};

export default ChangeContribution;
